import { Injectable } from '@angular/core';
import { USER_LS_KEY } from '../common.constants';
import { AuthResponse, User, UserModel } from '../common.interface';
import { UsersDataService } from './users-data.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { map, shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  user$ = new BehaviorSubject<User>(null);

  constructor(
    private http: HttpClient,
    private router: Router,
    private userDataService: UsersDataService,
  ) {
  }

  login(username: string, password: string): Observable<any> {
    return this.http.post<AuthResponse>(`${environment.apiUrls.auth}create/`, {
        username,
        password,
      }).pipe(
      shareReplay(),
      map(respData => {
        return this.handleAuthentication(
          username,
          respData.access,
          respData.refresh,
        );
      }),
    );
  }

  parseJwt(token) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
  }

  getRole(token): string {
    const role = this.parseJwt(token).role;
    return role || null;
  }

  autoLogin(redirectToLoginForm: boolean): void {
    const userData = JSON.parse(localStorage.getItem(USER_LS_KEY()));
    if (!userData) {
      this.clearCurrentUser(redirectToLoginForm);
      return;
    }

    const loadedUser = new User(
      userData.email,
      userData.id,
      userData.userRef,
      userData.clinicRef,
      userData.role,
      userData._tokenAccess,
      userData._tokenRefresh,
    );

    if (loadedUser.token) {
      this.user$.next(loadedUser);
      this.userDataService.setLoggedClinicRefOnly(loadedUser.clinicRef);
    }
  }

  setDeviceId(): string {
    let uId = localStorage.getItem('uId');

    if (!uId) {
      uId = window.navigator.userAgent.replace(/\D+/g, '');
      uId += Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('uId', uId);
    }

    return uId;
  }

  getDeviceId(): string {
    return localStorage.getItem('uId') || '';
  }

  private handleAuthentication(
    email: string,
    token: string,
    tokenRefresh: string,
  ): UserModel {
    const data = this.parseJwt(token);

    const user = new User(email, data.id, data.user_ref, data.clinic_ref, data.role, token, tokenRefresh);
    this.user$.next(user);
    localStorage.setItem(USER_LS_KEY(), JSON.stringify(user));
    return user;
  }

  logout(): void {
    // const deviceId = this.getDeviceId();
    // this.http
    //   .post<AuthResponse>(`${environment.apiUrls.auth}/logout`, {
    //     deviceId,
    //   }).pipe(
    //   finalize(() => this.clearCurrentUser()),
    // ).subscribe();
    this.clearCurrentUser(true);
  }

  clearCurrentUser(redirectToLoginForm: boolean): void {
    this.userDataService.clearClinicData();
    this.user$.next(null);
    localStorage.removeItem(USER_LS_KEY());
    if (redirectToLoginForm) {
      this.router.navigate(['/sign-in']);
    }
  }

  refreshToken(): Observable<UserModel> {
    const userData = JSON.parse(localStorage.getItem(USER_LS_KEY()));
    if (!userData) {
      return;
    }

    const email = userData.email;
    const access = userData._tokenAccess;
    const refresh = userData._tokenRefresh;

    return this.http.post<AuthResponse>(`${environment.apiUrls.auth}refresh/`, {
      access,
      refresh,
    }).pipe(
      map((respData): UserModel => {
          return this.handleAuthentication(
            email,
            respData.access,
            respData.refresh,
          );
        },
      ),
    );
  }
}

